import { useEffect, useState, useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { uploadManagedFile } from "../lib/files";
import { isOutboxSupported, listOutbox, subscribeOutbox, type OutboxInspection } from "./outbox";
import { syncOutbox, type SyncDeps, type SyncResult } from "./sync";

export const SYNC_INTERVAL_MS = 30_000;

export const realSyncDeps: SyncDeps = {
  createInspection: (payload) => api<{ id: string; code?: string }>("/inspections", { method: "POST", body: JSON.stringify(payload) }),
  uploadPhoto: async (inspectionId, photo) => {
    await uploadManagedFile({ kind: "inspection-photo", entityId: inspectionId, photoType: photo.type, label: photo.label, originalSize: photo.originalSize, width: photo.width, height: photo.height }, photo.blob, photo.fileName);
  },
  now: () => Date.now()
};

interface RuntimeState { online: boolean; syncing: boolean; lastSent: Array<{ id: string; code?: string }>; lastRunAt: number }

let state: RuntimeState = { online: typeof navigator === "undefined" ? true : navigator.onLine, syncing: false, lastSent: [], lastRunAt: 0 };
const subscribers = new Set<() => void>();

function setState(patch: Partial<RuntimeState>) {
  state = { ...state, ...patch };
  subscribers.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  subscribers.add(listener);
  return () => { subscribers.delete(listener); };
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => setState({ online: true }));
  window.addEventListener("offline", () => setState({ online: false }));
}

/** Rodar a fila agora (se houver rede). Seguro chamar várias vezes: só uma sincronização acontece por vez. */
export async function requestSync(userId: string, queryClient?: QueryClient, deps: SyncDeps = realSyncDeps): Promise<SyncResult | null> {
  if (!isOutboxSupported() || !navigator.onLine) return null;
  setState({ syncing: true });
  try {
    const result = await syncOutbox(userId, deps);
    if (result.sent.length) {
      setState({ lastSent: result.sent });
      if (queryClient) {
        await Promise.all(["inspections", "dashboard", "vehicles", "inspection-sequence"].map((key) => queryClient.invalidateQueries({ queryKey: [key], refetchType: "all" })));
      }
    }
    return result;
  } catch {
    return null;
  } finally {
    setState({ syncing: false, lastRunAt: Date.now() });
  }
}

/** Liga os gatilhos automáticos: ao abrir, ao voltar a internet, ao voltar para a aba e a cada 30 s. Retorna a função que desliga. */
export function startAutoSync(userId: string, queryClient: QueryClient): () => void {
  const run = () => { void requestSync(userId, queryClient); };
  const onVisible = () => { if (document.visibilityState === "visible") run(); };
  run();
  window.addEventListener("online", run);
  document.addEventListener("visibilitychange", onVisible);
  const timer = window.setInterval(run, SYNC_INTERVAL_MS);
  return () => { window.removeEventListener("online", run); document.removeEventListener("visibilitychange", onVisible); window.clearInterval(timer); };
}

export function useOfflineStatus(): RuntimeState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Itens da fila do usuário, atualizados quando a fila muda (nesta aba ou em outra). */
export function useOutboxItems(userId?: string): { items: OutboxInspection[]; loading: boolean } {
  const [items, setItems] = useState<OutboxInspection[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId || !isOutboxSupported()) { setItems([]); setLoading(false); return; }
    let active = true;
    const refresh = () => { void listOutbox(userId).then((next) => { if (active) { setItems(next); setLoading(false); } }).catch(() => { if (active) setLoading(false); }); };
    refresh();
    const unsubscribe = subscribeOutbox(refresh);
    return () => { active = false; unsubscribe(); };
  }, [userId]);
  return { items, loading };
}
